import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Flag, Pause, Play } from 'lucide-react';
import { useTestStore } from '../../stores/testStore';
import { LoadingSpinner } from '../ui';
import QuestionRenderer from './QuestionRenderer';
import SimpleProgressTracker from './SimpleProgressTracker';
import TimerComponent from './TimerComponent';

import type { TestResult } from '../../types/test';
import type { DetailedAnalysis } from '../../utils/resultAnalyzer';

interface TestEngineProps {
  testType: 'standard' | 'comprehensive';
  onComplete: (
    result: TestResult & { fullAnalysis?: DetailedAnalysis }
  ) => void;
  onExit: () => void;
}

const TestEngine: React.FC<TestEngineProps> = ({
  testType,
  onComplete,
  onExit,
}) => {
  const {
    currentSession,
    currentQuestion,
    // isTestActive,
    timeRemaining,
    answers,
    isLoading,
    error,
    startTest,
    submitAnswer,
    skipQuestion,
    nextQuestion,
    previousQuestion,
    goToQuestion,
    pauseTest,
    resumeTest,
    completeTest,
    abandonTest,
    updateTimeRemaining,
    setError,
  } = useTestStore();

  const [isPaused, setIsPaused] = useState(false);
  const [showConfirmExit, setShowConfirmExit] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [timeUpSubmitting, setTimeUpSubmitting] = useState(false);
  const [showTimeUpModal, setShowTimeUpModal] = useState(false);

  useEffect(() => {
    // Initialize test when component mounts
    const initializeTest = async () => {
      try {
        await startTest(testType);
      } catch (error) {
        console.error('Failed to initialize test:', error);
        // Don't throw the error - let the error state be handled by the component
      }
    };

    initializeTest();
  }, [testType, startTest]);

  const handleAnswer = async (answer: string | number, timeSpent: number) => {
    if (!currentQuestion) return;

    await submitAnswer(currentQuestion.id, answer, timeSpent);
  };

  const handleSkip = () => {
    if (!currentQuestion || isSkipping) return;

    setIsSkipping(true);
    const timeSpent = 1000; // Default time spent for skipped questions

    skipQuestion(currentQuestion.id, timeSpent);

    // Automatically move to next question after skipping
    if (
      currentSession &&
      currentSession.currentQuestionIndex < currentSession.questions.length - 1
    ) {
      nextQuestion();
    }

    // Reset the debounce after 1 second
    setTimeout(() => {
      setIsSkipping(false);
    }, 1000);
  };

  const handleNext = async () => {
    if (!currentSession) return;

    if (
      currentSession.currentQuestionIndex <
      currentSession.questions.length - 1
    ) {
      nextQuestion();
    } else {
      // Test completed
      const result = await completeTest();
      if (result) {
        onComplete(result);
      }
    }
  };

  const handlePrevious = () => {
    previousQuestion();
  };

  const handlePause = () => {
    if (isPaused) {
      resumeTest();
      setIsPaused(false);
    } else {
      pauseTest();
      setIsPaused(true);
    }
  };

  const handleTimeUp = async () => {
    if (!currentSession) return;

    // Show the time up modal instead of automatically submitting
    setShowTimeUpModal(true);
  };

  const handleSubmitTimeUpTest = async () => {
    if (!currentSession) return;

    setTimeUpSubmitting(true);
    setShowTimeUpModal(false);

    try {
      // Ensure all unanswered questions are marked as skipped/null
      const allQuestionIds = currentSession.questions.map(q => q.id);
      const answeredQuestionIds = answers.map(a => a.questionId);
      const unansweredQuestionIds = allQuestionIds.filter(id => !answeredQuestionIds.includes(id));

      // Mark unanswered questions as skipped (empty answer)
      for (const questionId of unansweredQuestionIds) {
        skipQuestion(questionId, 0); // 0 time spent since time ran out
      }

      // Complete the test with all questions accounted for
      const result = await completeTest();
      if (result) {
        onComplete(result);
      }
    } catch (error) {
      console.error('Error submitting test on time up:', error);
      setTimeUpSubmitting(false);
    }
  };

  const handleFinishTest = async () => {
    const result = await completeTest();
    if (result) {
      onComplete(result);
    }
  };

  const handleExit = () => {
    setShowConfirmExit(true);
  };

  const confirmExit = async () => {
    // Exit immediately without waiting for abandonTest to complete
    onExit();

    // Clean up the test session in the background
    if (currentSession && currentSession.status === 'in-progress') {
      abandonTest().catch(console.error);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Test Error
          </h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-4">
            <button
              onClick={() => setError(null)}
              className="flex-1 bg-purple-500 text-white px-6 py-3 rounded-full font-medium hover:bg-purple-600 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={onExit}
              className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-full font-medium hover:bg-gray-300 transition-colors"
            >
              Exit
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !currentSession || !currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="xl" className="mx-auto mb-6" />
          <p className="text-xl font-medium text-gray-700">
            {isLoading ? 'Preparing your test...' : 'Loading test...'}
          </p>
        </div>
      </div>
    );
  }

  const currentQuestionNumber = currentSession.currentQuestionIndex + 1;
  const totalQuestions = currentSession.questions.length;
  const selectedAnswer = answers.find(
    a => a.questionId === currentQuestion.id
  )?.selectedAnswer;

  // Check if all questions are answered (progress bar is full)
  const answeredCount = answers.filter(answer =>
    answer.selectedAnswer !== null && answer.selectedAnswer !== ''
  ).length;
  const allQuestionsAnswered = answeredCount === totalQuestions;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl animate-blob" />
        <div className="absolute top-0 right-0 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000" />
        <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000" />
      </div>

      <div className="relative z-10 container mx-auto px-6 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 gap-4 sm:gap-0">
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              {testType === 'standard'
                ? 'Standard IQ Test'
                : 'Comprehensive Deep Test'}
            </h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">
              Question {currentQuestionNumber} of {totalQuestions}
            </p>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto justify-end">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handlePause}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-full font-medium transition-all duration-300 text-sm sm:text-base flex-1 sm:flex-none justify-center ${isPaused
                ? 'bg-green-500 text-white shadow-lg'
                : 'bg-yellow-500 text-white shadow-lg'
                }`}
            >
              {isPaused ? <Play size={14} className="sm:w-4 sm:h-4" /> : <Pause size={14} className="sm:w-4 sm:h-4" />}
              <span className="hidden sm:inline">{isPaused ? 'Resume' : 'Pause'}</span>
              <span className="sm:hidden">{isPaused ? 'Resume' : 'Pause'}</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleExit}
              className="bg-red-500 text-white px-3 sm:px-4 py-2 rounded-full font-medium shadow-lg hover:shadow-xl transition-all duration-300 text-sm sm:text-base flex-1 sm:flex-none"
            >
              Exit
            </motion.button>
          </div>
        </div>

        {/* Mobile: Compact Timer Bar */}
        <div className="block lg:hidden mb-4">
          {(() => {
            // Calculate timer color states for mobile
            const percentage = currentSession ? (timeRemaining / currentSession.totalTimeLimit) * 100 : 100;
            const isWarning = percentage <= 25 && percentage > 10;
            const isCritical = percentage <= 10;

            const getTimerColor = () => {
              if (isCritical) return 'from-red-500 to-pink-500';
              if (isWarning) return 'from-yellow-500 to-orange-500';
              return 'from-blue-500 to-cyan-500';
            };

            const getTextColor = () => {
              if (isCritical) return 'text-red-600';
              if (isWarning) return 'text-yellow-600';
              return 'text-blue-600';
            };

            const getProgressColor = () => {
              if (isCritical) return 'from-red-500 to-pink-500';
              if (isWarning) return 'from-yellow-500 to-orange-500';
              return 'from-purple-500 to-pink-500';
            };

            return (
              <div className="bg-white/90 backdrop-blur-md rounded-xl p-3 shadow-lg border border-white/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <motion.div
                      animate={isCritical ? { scale: [1, 1.1, 1] } : {}}
                      transition={{ duration: 1, repeat: isCritical ? Infinity : 0 }}
                      className={`w-8 h-8 bg-gradient-to-r ${getTimerColor()} rounded-full flex items-center justify-center`}
                    >
                      {isCritical ? (
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                      )}
                    </motion.div>
                    <div>
                      <div className="text-xs text-gray-600">
                        {isCritical ? 'Critical Time!' : isWarning ? 'Time Warning' : 'Time Remaining'}
                      </div>
                      <motion.div
                        key={timeRemaining}
                        initial={{ scale: 1.1 }}
                        animate={{ scale: 1 }}
                        className={`text-sm font-bold ${getTextColor()}`}
                      >
                        {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
                      </motion.div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-600">Progress</div>
                    <div className="text-sm font-bold text-purple-600">
                      {answers.filter(a => a.selectedAnswer !== null && a.selectedAnswer !== '').length} / {totalQuestions}
                    </div>
                  </div>
                </div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                  <motion.div
                    className={`bg-gradient-to-r ${getProgressColor()} h-1.5 rounded-full transition-all duration-500`}
                    style={{ width: `${(answers.filter(a => a.selectedAnswer !== null && a.selectedAnswer !== '').length / totalQuestions) * 100}%` }}
                  />
                </div>

                {/* Mobile Warning Messages */}
                {(isWarning || isCritical) && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-2 overflow-hidden"
                  >
                    {isCritical && (
                      <div className="flex items-center gap-2 text-red-600 text-xs font-medium">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Critical: Less than 10% time remaining!
                      </div>
                    )}
                    {isWarning && !isCritical && (
                      <div className="flex items-center gap-2 text-yellow-600 text-xs font-medium">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        Warning: 25% time remaining
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            );
          })()}
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Desktop Sidebar - Timer & Progress */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="flex flex-col gap-6">
              <TimerComponent
                totalTime={currentSession.totalTimeLimit}
                onTimeUp={handleTimeUp}
                onTimeUpdate={updateTimeRemaining}
                isPaused={isPaused}
              />

              <SimpleProgressTracker
                currentQuestion={currentQuestionNumber}
                totalQuestions={totalQuestions}
                answers={answers}
                questions={currentSession.questions}
                onGoToQuestion={goToQuestion}
              />
            </div>
          </div>

          {/* Main Content - Question with consistent height */}
          <div className="lg:col-span-3">
            {/* Mobile Question Status - Moved above question */}
            <div className="block lg:hidden mb-4">
              <div className="bg-white/90 backdrop-blur-md rounded-xl p-3 shadow-lg border border-white/20">
                <div className="text-xs font-medium text-gray-600 mb-2">Question Status</div>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {Array.from({ length: Math.min(totalQuestions, 15) }, (_, index) => {
                    const questionNumber = index + 1;
                    const isCurrent = questionNumber === currentQuestionNumber;
                    const question = currentSession.questions[index];
                    const answer = answers.find(a => a.questionId === question?.id);
                    const isAnswered = !!answer && answer.selectedAnswer !== null && answer.selectedAnswer !== '';
                    const hasBeenVisited = !!answer;

                    const getStatusColor = () => {
                      if (isCurrent) return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white';
                      if (isAnswered) return 'bg-gradient-to-r from-blue-400 to-cyan-400 text-white';
                      if (hasBeenVisited) return 'bg-gray-300 text-gray-600';
                      return 'bg-gray-200 text-gray-500';
                    };

                    return (
                      <button
                        key={index}
                        onClick={() => {
                          if (goToQuestion && (hasBeenVisited || isCurrent)) {
                            goToQuestion(index);
                          }
                        }}
                        disabled={!hasBeenVisited && !isCurrent}
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${getStatusColor()} ${(hasBeenVisited || isCurrent) ? 'cursor-pointer' : 'cursor-default'
                          }`}
                      >
                        {questionNumber > 9 ? '•' : questionNumber}
                      </button>
                    );
                  })}
                  {totalQuestions > 15 && (
                    <div className="text-xs text-gray-500 ml-2">+{totalQuestions - 15} more</div>
                  )}
                </div>
              </div>
            </div>

            {/* Question container with consistent height */}
            <div className="min-h-[350px] sm:min-h-[400px] mb-4 sm:mb-6">
              <AnimatePresence mode="wait">
                <QuestionRenderer
                  key={currentQuestion.id}
                  question={currentQuestion}
                  questionNumber={currentQuestionNumber}
                  totalQuestions={totalQuestions}
                  onAnswer={handleAnswer}
                  selectedAnswer={selectedAnswer}
                />
              </AnimatePresence>
            </div>

            {/* Navigation */}
            <div className="space-y-4 mt-4 sm:mt-6 lg:relative lg:h-16">
              {/* Previous/Next Navigation - Corner alignment for desktop */}
              <div className="flex justify-between items-center gap-4 lg:absolute lg:inset-0 lg:flex lg:justify-between lg:items-center">
                {/* Previous Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handlePrevious}
                  disabled={currentQuestionNumber === 1}
                  className={`flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-medium transition-all duration-300 flex-1 sm:flex-none lg:flex-none text-sm ${currentQuestionNumber === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 shadow-lg hover:shadow-xl'
                    }`}
                >
                  <ChevronLeft className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
                  <span className="xs:hidden">Prev</span>
                  <span className="hidden xs:inline">Previous</span>
                </motion.button>

                {/* Smart Next/Skip/Finish Button */}
                <motion.button
                  whileHover={!isSkipping && !isLoading ? { scale: 1.05 } : {}}
                  whileTap={!isSkipping && !isLoading ? { scale: 0.95 } : {}}
                  onClick={() => {
                    if (allQuestionsAnswered || currentQuestionNumber === totalQuestions) {
                      handleFinishTest();
                    } else if (selectedAnswer) {
                      handleNext();
                    } else {
                      handleSkip();
                    }
                  }}
                  disabled={isSkipping || isLoading}
                  className={`flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-medium transition-all duration-300 flex-1 sm:flex-none lg:flex-none text-sm ${isSkipping || isLoading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : allQuestionsAnswered || currentQuestionNumber === totalQuestions
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg hover:shadow-xl'
                      : selectedAnswer
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg hover:shadow-xl'
                        : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-gray-200 hover:to-gray-300 shadow-md hover:shadow-lg'
                    }`}
                >
                  {/* Icon Logic */}
                  {!isSkipping && !isLoading && (
                    <>
                      {allQuestionsAnswered || currentQuestionNumber === totalQuestions ? (
                        <Flag className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
                      ) : selectedAnswer ? (
                        <ChevronRight className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
                      ) : null}
                    </>
                  )}

                  {/* Text for small screens */}
                  <span className="xs:hidden">
                    {isSkipping
                      ? 'Skip...'
                      : isLoading
                        ? 'Done...'
                        : allQuestionsAnswered || currentQuestionNumber === totalQuestions
                          ? 'Finish'
                          : selectedAnswer
                            ? 'Next'
                            : 'Skip'}
                  </span>
                  {/* Text for larger screens */}
                  <span className="hidden xs:inline">
                    {isSkipping
                      ? 'Skipping...'
                      : isLoading
                        ? 'Finishing...'
                        : allQuestionsAnswered || currentQuestionNumber === totalQuestions
                          ? 'Finish'
                          : selectedAnswer
                            ? 'Next'
                            : 'Skip'}
                  </span>
                </motion.button>
              </div>

              {/* Status Text - Bottom section */}
              <div className="text-center pt-2">
                <p className="text-xs sm:text-sm text-gray-500">
                  {allQuestionsAnswered
                    ? 'All questions answered - ready to finish!'
                    : currentQuestionNumber === totalQuestions
                      ? 'Last question - skip to finish or answer to complete'
                      : `${totalQuestions - currentQuestionNumber} questions remaining`}
                </p>
              </div>
            </div>
          </div>
        </div>


      </div>

      {/* Pause Overlay */}
      <AnimatePresence>
        {isPaused && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-8 shadow-2xl text-center max-w-md mx-4"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <Pause size={24} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Test Paused
              </h3>
              <p className="text-gray-600 mb-6">
                Take your time. Click resume when you're ready to continue.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePause}
                className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-3 rounded-full font-bold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Resume Test
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-8 shadow-2xl text-center max-w-md mx-4"
            >
              <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Processing...
              </h3>
              <p className="text-gray-600">
                Please wait while we process your request.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Time Up Overlay */}
      <AnimatePresence>
        {timeUpSubmitting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-8 shadow-2xl text-center max-w-md mx-4"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-red-400 to-orange-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Time's Up!
              </h3>
              <p className="text-gray-600 mb-4">
                Submitting your test with current answers...
              </p>
              <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Time Up Modal */}
      <AnimatePresence>
        {showTimeUpModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-8 shadow-2xl text-center max-w-md mx-4"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-orange-400 to-red-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Time is Up!
              </h3>
              <p className="text-gray-600 mb-6">
                Your time has expired. Click below to submit your test with your current answers.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSubmitTimeUpTest}
                disabled={timeUpSubmitting}
                className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-3 rounded-full font-bold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {timeUpSubmitting ? 'Submitting...' : 'Submit Your Result'}
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Exit Confirmation */}
      <AnimatePresence>
        {showConfirmExit && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-8 shadow-2xl text-center max-w-md mx-4"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-red-400 to-pink-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <Flag size={24} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Exit Test?
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to exit? Your progress will be lost.
              </p>
              <div className="flex gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowConfirmExit(false)}
                  disabled={isLoading}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-full font-medium hover:bg-gray-300 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={confirmExit}
                  disabled={isLoading}
                  className="flex-1 bg-gradient-to-r from-red-500 to-pink-500 text-white py-3 rounded-full font-bold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Exiting...' : 'Exit'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TestEngine;
